import React, { useState, useEffect, useCallback } from 'react';
import { 
  CreditCard, BarChart3, Users, Terminal, Link2, Settings, LogOut, 
  Plus, Search, Filter, Download, RefreshCw, Copy, Check, QrCode,
  ArrowUpRight, ArrowDownLeft, TrendingUp, TrendingDown, DollarSign,
  Smartphone, Monitor, Wifi, WifiOff, Shield, Eye, EyeOff, Key,
  Bell, Globe, ChevronRight, MoreVertical, Calendar, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const API = process.env.REACT_APP_BACKEND_URL;

// Card network colors
const CARD_COLORS = {
  visa: '#1A1F71',
  mastercard: '#EB001B',
  maestro: '#0099DF',
  amex: '#006FCF',
  unionpay: '#D32029',
  discover: '#FF6000'
};

const MerchantPortal = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [merchant, setMerchant] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('merchant_token'));
  
  // UI state
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [showApiKeys, setShowApiKeys] = useState(false);
  
  // Data state
  const [analytics, setAnalytics] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [paymentLinks, setPaymentLinks] = useState([]);
  
  // Modal state
  const [showCreatePaymentLink, setShowCreatePaymentLink] = useState(false);
  const [showCreateTerminal, setShowCreateTerminal] = useState(false);
  const [showVirtualPOS, setShowVirtualPOS] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Auth form state
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    business_name: '',
    business_type: 'retail',
    country: 'GB',
    currency: 'EUR'
  });

  const getAuthHeader = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  // Check authentication on mount
  useEffect(() => {
    if (token) {
      checkAuth();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/api/merchant/me`, {
        headers: getAuthHeader()
      });
      setMerchant(response.data);
      setIsAuthenticated(true);
      loadDashboardData();
    } catch (error) {
      localStorage.removeItem('merchant_token');
      setToken(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      const [analyticsRes, transactionsRes, terminalsRes, linksRes] = await Promise.all([
        axios.get(`${API}/api/merchant/analytics/summary?period=month`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/merchant/transactions?limit=10`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/merchant/terminals`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/merchant/payment-links`, { headers: getAuthHeader() })
      ]);
      
      setAnalytics(analyticsRes.data);
      setTransactions(transactionsRes.data.transactions || []);
      setTerminals(terminalsRes.data.terminals || []);
      setPaymentLinks(linksRes.data.payment_links || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/merchant/login`, {
        email: authForm.email,
        password: authForm.password
      });
      
      localStorage.setItem('merchant_token', response.data.token);
      setToken(response.data.token);
      setMerchant(response.data.merchant);
      setIsAuthenticated(true);
      toast.success('Welcome back!');
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/merchant/register`, {
        business_name: authForm.business_name,
        business_email: authForm.email,
        password: authForm.password,
        business_type: authForm.business_type,
        country: authForm.country,
        currency: authForm.currency
      });
      
      localStorage.setItem('merchant_token', response.data.token);
      setToken(response.data.token);
      setIsAuthenticated(true);
      toast.success('Account created! Your API keys have been generated.');
      checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('merchant_token');
    setToken(null);
    setIsAuthenticated(false);
    setMerchant(null);
    toast.success('Logged out');
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success('Copied to clipboard');
  };

  const createPaymentLink = async (amount, currency, description) => {
    try {
      const response = await axios.post(`${API}/api/merchant/payment-links`, {
        amount: parseFloat(amount),
        currency,
        description
      }, { headers: getAuthHeader() });
      
      setPaymentLinks([response.data, ...paymentLinks]);
      setShowCreatePaymentLink(false);
      toast.success('Payment link created!');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment link');
    }
  };

  const createTerminal = async (data) => {
    try {
      const response = await axios.post(`${API}/api/merchant/terminals`, data, {
        headers: getAuthHeader()
      });
      
      setTerminals([response.data, ...terminals]);
      setShowCreateTerminal(false);
      toast.success('Terminal registered!');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create terminal');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  // Auth screens
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-purple-950/20 to-gray-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">Onerik</span>
            </div>
            <p className="text-white/60">Merchant Portal</p>
          </div>

          {/* Auth Card */}
          <div className="bg-black/40 border border-white/10 rounded-2xl p-6">
            {/* Tabs */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setAuthMode('login')}
                className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                  authMode === 'login' 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setAuthMode('register')}
                className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                  authMode === 'register' 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-white/5 text-white/60 hover:bg-white/10'
                }`}
              >
                Register
              </button>
            </div>

            {authMode === 'login' ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="text-sm text-white/60 mb-1 block">Email</label>
                  <input
                    type="email"
                    value={authForm.email}
                    onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-white/40 focus:outline-none focus:border-purple-500"
                    placeholder="merchant@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-white/60 mb-1 block">Password</label>
                  <input
                    type="password"
                    value={authForm.password}
                    onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-white/40 focus:outline-none focus:border-purple-500"
                    placeholder="••••••••"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium py-3 rounded-xl hover:opacity-90 transition-all"
                >
                  Sign In
                </button>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="text-sm text-white/60 mb-1 block">Business Name</label>
                  <input
                    type="text"
                    value={authForm.business_name}
                    onChange={(e) => setAuthForm({...authForm, business_name: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-white/40 focus:outline-none focus:border-purple-500"
                    placeholder="Your Business Ltd"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-white/60 mb-1 block">Business Email</label>
                  <input
                    type="email"
                    value={authForm.email}
                    onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-white/40 focus:outline-none focus:border-purple-500"
                    placeholder="contact@yourbusiness.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-white/60 mb-1 block">Password</label>
                  <input
                    type="password"
                    value={authForm.password}
                    onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-white/40 focus:outline-none focus:border-purple-500"
                    placeholder="••••••••"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-white/60 mb-1 block">Business Type</label>
                    <select
                      value={authForm.business_type}
                      onChange={(e) => setAuthForm({...authForm, business_type: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-purple-500"
                    >
                      <option value="retail">Retail</option>
                      <option value="ecommerce">E-commerce</option>
                      <option value="restaurant">Restaurant</option>
                      <option value="services">Services</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-white/60 mb-1 block">Currency</label>
                    <select
                      value={authForm.currency}
                      onChange={(e) => setAuthForm({...authForm, currency: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-purple-500"
                    >
                      <option value="EUR">EUR €</option>
                      <option value="GBP">GBP £</option>
                      <option value="USD">USD $</option>
                    </select>
                  </div>
                </div>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium py-3 rounded-xl hover:opacity-90 transition-all"
                >
                  Create Account
                </button>
              </form>
            )}
          </div>

          {/* Features */}
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { icon: CreditCard, label: 'Card Payments' },
              { icon: Terminal, label: 'POS Ready' },
              { icon: Shield, label: 'Secure' }
            ].map((feature, i) => (
              <div key={i} className="text-center">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center mx-auto mb-2">
                  <feature.icon className="w-5 h-5 text-purple-400" />
                </div>
                <p className="text-xs text-white/60">{feature.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Sidebar */}
      <div className="w-64 bg-black/40 border-r border-white/10 p-4 flex flex-col">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-8 px-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white">Onerik</span>
          <span className="text-xs text-white/40 ml-auto">Merchant</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1">
          {[
            { id: 'dashboard', icon: BarChart3, label: 'Dashboard' },
            { id: 'transactions', icon: ArrowUpRight, label: 'Transactions' },
            { id: 'virtual-pos', icon: Monitor, label: 'Virtual POS' },
            { id: 'terminals', icon: Terminal, label: 'Terminals' },
            { id: 'payment-links', icon: Link2, label: 'Payment Links' },
            { id: 'customers', icon: Users, label: 'Customers' },
            { id: 'developers', icon: Key, label: 'Developers' },
            { id: 'settings', icon: Settings, label: 'Settings' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                activeTab === item.id 
                  ? 'bg-purple-600 text-white' 
                  : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* User section */}
        <div className="pt-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-white font-bold">
              {merchant?.business_name?.charAt(0) || 'M'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{merchant?.business_name}</p>
              <p className="text-xs text-white/40 truncate">{merchant?.business_email}</p>
            </div>
          </div>
          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-auto">
        {activeTab === 'dashboard' && (
          <DashboardView 
            analytics={analytics} 
            transactions={transactions}
            terminals={terminals}
            onRefresh={loadDashboardData}
          />
        )}
        
        {activeTab === 'transactions' && (
          <TransactionsView 
            transactions={transactions}
            getAuthHeader={getAuthHeader}
          />
        )}
        
        {activeTab === 'virtual-pos' && (
          <VirtualPOSView 
            merchant={merchant}
            getAuthHeader={getAuthHeader}
          />
        )}
        
        {activeTab === 'terminals' && (
          <TerminalsView 
            terminals={terminals}
            onCreate={() => setShowCreateTerminal(true)}
            getAuthHeader={getAuthHeader}
          />
        )}
        
        {activeTab === 'payment-links' && (
          <PaymentLinksView 
            links={paymentLinks}
            onCreate={() => setShowCreatePaymentLink(true)}
            copyToClipboard={copyToClipboard}
            copiedField={copiedField}
          />
        )}
        
        {activeTab === 'customers' && (
          <CustomersView 
            customers={customers}
            getAuthHeader={getAuthHeader}
          />
        )}
        
        {activeTab === 'developers' && (
          <DevelopersView 
            merchant={merchant}
            showApiKeys={showApiKeys}
            setShowApiKeys={setShowApiKeys}
            copyToClipboard={copyToClipboard}
            copiedField={copiedField}
            getAuthHeader={getAuthHeader}
          />
        )}
        
        {activeTab === 'settings' && (
          <SettingsView 
            merchant={merchant}
            getAuthHeader={getAuthHeader}
            onUpdate={checkAuth}
          />
        )}
      </div>

      {/* Create Payment Link Modal */}
      {showCreatePaymentLink && (
        <CreatePaymentLinkModal 
          onClose={() => setShowCreatePaymentLink(false)}
          onCreate={createPaymentLink}
          currency={merchant?.default_currency || 'EUR'}
        />
      )}

      {/* Create Terminal Modal */}
      {showCreateTerminal && (
        <CreateTerminalModal 
          onClose={() => setShowCreateTerminal(false)}
          onCreate={createTerminal}
        />
      )}
    </div>
  );
};

// Dashboard View Component
const DashboardView = ({ analytics, transactions, terminals, onRefresh }) => {
  const stats = [
    { 
      label: 'Gross Volume', 
      value: `€${(analytics?.gross_volume || 0).toLocaleString()}`,
      change: '+12.5%',
      positive: true,
      icon: DollarSign
    },
    { 
      label: 'Transactions', 
      value: analytics?.total_transactions || 0,
      change: '+8.2%',
      positive: true,
      icon: ArrowUpRight
    },
    { 
      label: 'Success Rate', 
      value: `${analytics?.success_rate || 0}%`,
      change: '+2.1%',
      positive: true,
      icon: TrendingUp
    },
    { 
      label: 'Net Revenue', 
      value: `€${(analytics?.net_volume || 0).toLocaleString()}`,
      change: '+15.3%',
      positive: true,
      icon: BarChart3
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-white/60">Overview of your payment activity</p>
        </div>
        <button 
          onClick={onRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white hover:bg-white/10 transition-all"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <div key={i} className="bg-black/40 border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <stat.icon className="w-5 h-5 text-purple-400" />
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded-lg ${
                stat.positive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {stat.change}
              </span>
            </div>
            <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
            <p className="text-sm text-white/60">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Charts and Activity */}
      <div className="grid grid-cols-3 gap-6">
        {/* Recent Transactions */}
        <div className="col-span-2 bg-black/40 border border-white/10 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white">Recent Transactions</h2>
            <button className="text-purple-400 text-sm hover:text-purple-300">View All</button>
          </div>
          <div className="space-y-3">
            {transactions.slice(0, 5).map((txn, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    txn.status === 'completed' ? 'bg-green-500/20' : 'bg-red-500/20'
                  }`}>
                    {txn.status === 'completed' ? (
                      <ArrowDownLeft className="w-5 h-5 text-green-400" />
                    ) : (
                      <ArrowUpRight className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">
                      •••• {txn.card_last_four || '****'}
                    </p>
                    <p className="text-xs text-white/40">
                      {txn.card_network?.toUpperCase() || 'CARD'} • {new Date(txn.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-white">
                    {txn.currency} {txn.amount?.toFixed(2)}
                  </p>
                  <p className={`text-xs ${
                    txn.status === 'completed' ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {txn.status}
                  </p>
                </div>
              </div>
            ))}
            {transactions.length === 0 && (
              <div className="text-center py-8 text-white/40">
                <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No transactions yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Terminals Status */}
        <div className="bg-black/40 border border-white/10 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white">Terminals</h2>
            <span className="text-xs text-white/40">{terminals.length} registered</span>
          </div>
          <div className="space-y-3">
            {terminals.slice(0, 4).map((terminal, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    terminal.status === 'active' ? 'bg-green-500/20' : 'bg-yellow-500/20'
                  }`}>
                    {terminal.terminal_type === 'virtual' ? (
                      <Monitor className="w-4 h-4 text-white/60" />
                    ) : (
                      <Terminal className="w-4 h-4 text-white/60" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{terminal.location_name}</p>
                    <p className="text-xs text-white/40">{terminal.terminal_type}</p>
                  </div>
                </div>
                <div className={`w-2 h-2 rounded-full ${
                  terminal.status === 'active' ? 'bg-green-400' : 'bg-yellow-400'
                }`}></div>
              </div>
            ))}
            {terminals.length === 0 && (
              <div className="text-center py-6 text-white/40">
                <Terminal className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No terminals yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card Networks */}
      <div className="bg-black/40 border border-white/10 rounded-2xl p-5">
        <h2 className="font-semibold text-white mb-4">Accepted Cards</h2>
        <div className="flex gap-3">
          {Object.entries(CARD_COLORS).map(([network, color]) => (
            <div 
              key={network}
              className="px-4 py-2 rounded-lg text-sm font-bold text-white"
              style={{ backgroundColor: color }}
            >
              {network.toUpperCase()}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Virtual POS View
const VirtualPOSView = ({ merchant, getAuthHeader }) => {
  const [amount, setAmount] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const processPayment = async () => {
    if (!amount || !cardNumber || !expiry || !cvv || !cardholderName) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsProcessing(true);
    setResult(null);

    try {
      // Create payment intent
      const intentRes = await axios.post(`${API}/api/merchant/payment-intents`, {
        amount: parseFloat(amount),
        currency: merchant?.default_currency || 'EUR',
        description: 'Virtual POS Payment'
      }, { headers: getAuthHeader() });

      // Process payment
      const [month, year] = expiry.split('/');
      const paymentRes = await axios.post(
        `${API}/api/merchant/payment-intents/${intentRes.data.id}/confirm`,
        {
          payment_intent_id: intentRes.data.id,
          card_number: cardNumber.replace(/\s/g, ''),
          expiry_month: parseInt(month),
          expiry_year: parseInt('20' + year),
          cvv,
          cardholder_name: cardholderName
        },
        { headers: getAuthHeader() }
      );

      setResult(paymentRes.data);
      if (paymentRes.data.success) {
        toast.success('Payment successful!');
        // Clear form
        setAmount('');
        setCardNumber('');
        setExpiry('');
        setCvv('');
        setCardholderName('');
      } else {
        toast.error(paymentRes.data.response_message || 'Payment failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment processing failed');
      setResult({ success: false, response_message: error.response?.data?.detail || 'Error' });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Virtual POS</h1>
        <p className="text-white/60">Process card payments directly</p>
      </div>

      <div className="bg-black/40 border border-white/10 rounded-2xl p-6">
        {/* Amount */}
        <div className="mb-6">
          <label className="text-sm text-white/60 mb-2 block">Amount</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 text-xl">
              {merchant?.default_currency === 'GBP' ? '£' : merchant?.default_currency === 'USD' ? '$' : '€'}
            </span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full bg-white/5 border border-white/10 rounded-xl h-16 pl-12 pr-4 text-2xl font-bold text-white placeholder:text-white/20 focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>

        {/* Card Details */}
        <div className="space-y-4">
          <div>
            <label className="text-sm text-white/60 mb-2 block">Card Number</label>
            <input
              type="text"
              value={cardNumber}
              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
              placeholder="4242 4242 4242 4242"
              maxLength={19}
              className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-white/40 focus:outline-none focus:border-purple-500 font-mono"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-white/60 mb-2 block">Expiry</label>
              <input
                type="text"
                value={expiry}
                onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                placeholder="MM/YY"
                maxLength={5}
                className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-white/40 focus:outline-none focus:border-purple-500 font-mono"
              />
            </div>
            <div>
              <label className="text-sm text-white/60 mb-2 block">CVV</label>
              <input
                type="password"
                value={cvv}
                onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="•••"
                maxLength={4}
                className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-white/40 focus:outline-none focus:border-purple-500 font-mono"
              />
            </div>
            <div>
              <label className="text-sm text-white/60 mb-2 block">Cardholder</label>
              <input
                type="text"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value.toUpperCase())}
                placeholder="NAME"
                className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-white/40 focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>
        </div>

        {/* Accepted Cards */}
        <div className="flex gap-2 my-6">
          {Object.entries(CARD_COLORS).slice(0, 5).map(([network, color]) => (
            <div 
              key={network}
              className="px-2 py-1 rounded text-xs font-bold text-white opacity-60"
              style={{ backgroundColor: color }}
            >
              {network.toUpperCase()}
            </div>
          ))}
        </div>

        {/* Process Button */}
        <button
          onClick={processPayment}
          disabled={isProcessing}
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium py-4 rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isProcessing ? (
            <>
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Process Payment
            </>
          )}
        </button>

        {/* Result */}
        {result && (
          <div className={`mt-4 p-4 rounded-xl ${
            result.success ? 'bg-green-500/20 border border-green-500/30' : 'bg-red-500/20 border border-red-500/30'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              {result.success ? (
                <Check className="w-5 h-5 text-green-400" />
              ) : (
                <span className="w-5 h-5 text-red-400">✕</span>
              )}
              <span className={`font-medium ${result.success ? 'text-green-400' : 'text-red-400'}`}>
                {result.success ? 'Payment Approved' : 'Payment Declined'}
              </span>
            </div>
            {result.transaction_id && (
              <p className="text-sm text-white/60">Transaction ID: {result.transaction_id}</p>
            )}
            {result.authorization_code && (
              <p className="text-sm text-white/60">Auth Code: {result.authorization_code}</p>
            )}
            {!result.success && result.response_message && (
              <p className="text-sm text-red-400">{result.response_message}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Placeholder views for other tabs
const TransactionsView = ({ transactions, getAuthHeader }) => (
  <div>
    <h1 className="text-2xl font-bold text-white mb-6">Transactions</h1>
    <div className="bg-black/40 border border-white/10 rounded-2xl overflow-hidden">
      <table className="w-full">
        <thead className="bg-white/5">
          <tr>
            <th className="text-left text-white/60 text-sm font-medium p-4">Transaction ID</th>
            <th className="text-left text-white/60 text-sm font-medium p-4">Amount</th>
            <th className="text-left text-white/60 text-sm font-medium p-4">Card</th>
            <th className="text-left text-white/60 text-sm font-medium p-4">Status</th>
            <th className="text-left text-white/60 text-sm font-medium p-4">Date</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((txn, i) => (
            <tr key={i} className="border-t border-white/5">
              <td className="p-4 text-white font-mono text-sm">{txn.transaction_id?.slice(0, 16)}...</td>
              <td className="p-4 text-white">{txn.currency} {txn.amount?.toFixed(2)}</td>
              <td className="p-4 text-white/60">•••• {txn.card_last_four}</td>
              <td className="p-4">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  txn.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {txn.status}
                </span>
              </td>
              <td className="p-4 text-white/60 text-sm">{new Date(txn.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const TerminalsView = ({ terminals, onCreate, getAuthHeader }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <h1 className="text-2xl font-bold text-white">Terminals</h1>
      <button 
        onClick={onCreate}
        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all"
      >
        <Plus className="w-4 h-4" />
        Add Terminal
      </button>
    </div>
    <div className="grid grid-cols-3 gap-4">
      {terminals.map((terminal, i) => (
        <div key={i} className="bg-black/40 border border-white/10 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Terminal className="w-6 h-6 text-purple-400" />
            </div>
            <div className={`w-3 h-3 rounded-full ${
              terminal.status === 'active' ? 'bg-green-400' : 'bg-yellow-400'
            }`}></div>
          </div>
          <h3 className="font-medium text-white mb-1">{terminal.location_name}</h3>
          <p className="text-sm text-white/40 mb-3">{terminal.terminal_type}</p>
          <p className="text-xs text-white/40 font-mono">{terminal.terminal_id}</p>
        </div>
      ))}
    </div>
  </div>
);

const PaymentLinksView = ({ links, onCreate, copyToClipboard, copiedField }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <h1 className="text-2xl font-bold text-white">Payment Links</h1>
      <button 
        onClick={onCreate}
        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all"
      >
        <Plus className="w-4 h-4" />
        Create Link
      </button>
    </div>
    <div className="space-y-4">
      {links.map((link, i) => (
        <div key={i} className="bg-black/40 border border-white/10 rounded-2xl p-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Link2 className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="font-medium text-white">{link.currency} {link.amount?.toFixed(2)}</p>
              <p className="text-sm text-white/40">{link.description || 'No description'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-sm text-white/60 font-mono">{link.url?.slice(0, 30)}...</p>
            <button 
              onClick={() => copyToClipboard(link.url, link.link_id)}
              className="p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-all"
            >
              {copiedField === link.link_id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-white/60" />}
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const CustomersView = ({ customers, getAuthHeader }) => (
  <div>
    <h1 className="text-2xl font-bold text-white mb-6">Customers</h1>
    <div className="bg-black/40 border border-white/10 rounded-2xl p-8 text-center">
      <Users className="w-12 h-12 mx-auto mb-4 text-white/20" />
      <p className="text-white/60">Customer management coming soon</p>
    </div>
  </div>
);

const DevelopersView = ({ merchant, showApiKeys, setShowApiKeys, copyToClipboard, copiedField, getAuthHeader }) => (
  <div>
    <h1 className="text-2xl font-bold text-white mb-6">API & Developers</h1>
    
    <div className="space-y-6">
      {/* API Keys */}
      <div className="bg-black/40 border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-white">API Keys</h2>
          <button 
            onClick={() => setShowApiKeys(!showApiKeys)}
            className="text-purple-400 text-sm hover:text-purple-300 flex items-center gap-1"
          >
            {showApiKeys ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showApiKeys ? 'Hide' : 'Show'}
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="text-sm text-white/60 mb-2 block">API Key (Public)</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white font-mono">
                {showApiKeys ? merchant?.api_key : '••••••••••••••••••••••••'}
              </code>
              <button 
                onClick={() => copyToClipboard(merchant?.api_key, 'apikey')}
                className="p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-all"
              >
                {copiedField === 'apikey' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-white/60" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Webhooks */}
      <div className="bg-black/40 border border-white/10 rounded-2xl p-6">
        <h2 className="font-semibold text-white mb-4">Webhooks</h2>
        <div>
          <label className="text-sm text-white/60 mb-2 block">Webhook URL</label>
          <input
            type="url"
            placeholder="https://your-server.com/webhook"
            className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-white/40 focus:outline-none focus:border-purple-500"
          />
        </div>
      </div>

      {/* API Documentation */}
      <div className="bg-black/40 border border-white/10 rounded-2xl p-6">
        <h2 className="font-semibold text-white mb-4">Quick Start</h2>
        <pre className="bg-white/5 rounded-xl p-4 overflow-x-auto text-sm">
          <code className="text-green-400">{`curl -X POST ${API}/api/merchant/payment-intents \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"amount": 10.00, "currency": "EUR"}'`}</code>
        </pre>
      </div>
    </div>
  </div>
);

const SettingsView = ({ merchant, getAuthHeader, onUpdate }) => (
  <div>
    <h1 className="text-2xl font-bold text-white mb-6">Settings</h1>
    <div className="bg-black/40 border border-white/10 rounded-2xl p-6">
      <h2 className="font-semibold text-white mb-4">Business Information</h2>
      <div className="space-y-4">
        <div>
          <label className="text-sm text-white/60 mb-2 block">Business Name</label>
          <input
            type="text"
            defaultValue={merchant?.business_name}
            className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-purple-500"
          />
        </div>
        <div>
          <label className="text-sm text-white/60 mb-2 block">Business Email</label>
          <input
            type="email"
            defaultValue={merchant?.business_email}
            disabled
            className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white/60 cursor-not-allowed"
          />
        </div>
      </div>
    </div>
  </div>
);

// Create Payment Link Modal
const CreatePaymentLinkModal = ({ onClose, onCreate, currency }) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!amount) {
      toast.error('Please enter an amount');
      return;
    }
    setIsCreating(true);
    await onCreate(amount, currency, description);
    setIsCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold text-white mb-4">Create Payment Link</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-white/60 mb-2 block">Amount ({currency})</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-purple-500"
            />
          </div>
          <div>
            <label className="text-sm text-white/60 mb-2 block">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Payment for..."
              className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-white/5 text-white rounded-xl hover:bg-white/10 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreating}
            className="flex-1 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all disabled:opacity-50"
          >
            {isCreating ? 'Creating...' : 'Create Link'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Create Terminal Modal
const CreateTerminalModal = ({ onClose, onCreate }) => {
  const [terminalType, setTerminalType] = useState('countertop');
  const [locationName, setLocationName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!locationName) {
      toast.error('Please enter a location name');
      return;
    }
    setIsCreating(true);
    await onCreate({ terminal_type: terminalType, location_name: locationName });
    setIsCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold text-white mb-4">Register Terminal</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-white/60 mb-2 block">Terminal Type</label>
            <select
              value={terminalType}
              onChange={(e) => setTerminalType(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-purple-500"
            >
              <option value="countertop">Countertop</option>
              <option value="portable">Portable</option>
              <option value="mobile">Mobile</option>
              <option value="virtual">Virtual</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-white/60 mb-2 block">Location Name</label>
            <input
              type="text"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              placeholder="Main Store, Checkout 1, etc."
              className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-4 text-white focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-white/5 text-white rounded-xl hover:bg-white/10 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreating}
            className="flex-1 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all disabled:opacity-50"
          >
            {isCreating ? 'Registering...' : 'Register Terminal'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MerchantPortal;
